import { ref } from 'vue'
import { apiPost, apiDelete } from '../api/client'

// Annotated text, keyed by the exact source string. Stories and grammar cards
// re-render often and the same line is annotated identically every time, so the
// cache means one request per distinct line for the life of the page.
const cache = new Map()

// In-flight requests, keyed by text. A parent batches a whole story while each
// line's TappableText may also ask for its own text before that batch lands;
// without this they'd all issue separate POSTs for the same lines.
const pending = new Map()

// Which words are in the learner's deck. Kept apart from the cached tokens so a
// save updates every rendering of that word at once, instead of only the one
// that was tapped.
export const savedWordIds = ref(new Set())

// The open popover, shared so only one word is ever expanded at a time.
export const activeWord = ref(null)

let ownerSeq = 0
export function nextOwnerId() {
  return ++ownerSeq
}

/**
 * Annotates texts into tappable tokens, returning one token array per input in
 * the order given. Lookup is an enhancement: on failure the text still renders,
 * just without tappable words.
 */
export async function annotateTexts(texts) {
  const wanted = texts.map(t => (typeof t === 'string' ? t : ''))
  const needed = [...new Set(wanted.filter(t => t.trim() && !cache.has(t) && !pending.has(t)))]

  if (needed.length) {
    const request = apiPost('/api/v1/lookup/annotate', { texts: needed })
      .then((data) => {
        const tokens = data?.tokens ?? []
        const saved = new Set(savedWordIds.value)

        needed.forEach((text, i) => {
          const line = tokens[i] ?? [{ text }]
          cache.set(text, line)
          for (const token of line) {
            if (token.word?.saved) saved.add(token.word.id)
          }
        })
        savedWordIds.value = saved
      })
      .catch(() => {
        // Lookup is an enhancement: cache the plain text so the line still
        // renders and we don't retry it on every re-render.
        needed.forEach(text => cache.set(text, [{ text }]))
      })
      .finally(() => {
        needed.forEach(text => pending.delete(text))
      })

    needed.forEach(text => pending.set(text, request))
  }

  // Wait on whatever is in flight for the texts we were asked about, whether
  // this call started it or another one did.
  const inFlight = [...new Set(wanted.map(text => pending.get(text)).filter(Boolean))]
  if (inFlight.length) await Promise.allSettled(inFlight)

  return wanted.map(text => cache.get(text) ?? [{ text }])
}

export function isSaved(wordId) {
  return savedWordIds.value.has(wordId)
}

export function openWord(ownerId, word, rect) {
  activeWord.value = { ownerId, word, rect }
}

export function closeWord() {
  activeWord.value = null
}

/**
 * Toggles a word in the deck. `source` tags where it was saved from so the deck
 * can later be scoped to one text ({ kind: 'story', id: 12 }).
 */
export async function toggleSaved(word, source = {}) {
  const currentlySaved = isSaved(word.id)
  const next = new Set(savedWordIds.value)

  // Flip optimistically — the popover should feel instant — and roll back if
  // the request fails.
  currentlySaved ? next.delete(word.id) : next.add(word.id)
  savedWordIds.value = next

  try {
    if (currentlySaved) {
      await apiDelete(`/api/v1/deck/${word.id}`)
    } else {
      await apiPost('/api/v1/deck', {
        vocabulary_word_id: word.id,
        source_kind: source.kind ?? null,
        source_id: source.id ?? null,
      })
    }
    return !currentlySaved
  } catch (e) {
    const rolledBack = new Set(savedWordIds.value)
    currentlySaved ? rolledBack.add(word.id) : rolledBack.delete(word.id)
    savedWordIds.value = rolledBack
    return currentlySaved
  }
}
